<script setup lang="ts">
/**
 * `sponsor-strip` zone: sponsor logos, rotating.
 *
 * Logos are always loaded through an `<img>` with contain-fit and never inlined
 * (D15). Sponsor artwork is very often SVG, and an inlined SVG can carry script;
 * `<img>` neutralises that without a sanitiser. That is also why `logoDarkUrl`
 * exists — the alternative to a second file would be recolouring at render time,
 * which means inlining.
 *
 * The visible page is derived from the injected clock rather than an internal
 * timer: rotation is then a pure function of wall-clock time, so every screen in
 * a venue turns its page on the same beat, and a test can render any page by
 * choosing `now`.
 *
 * Rotation is tier-weighted: `sponsorPages` builds a cycle in which a presenting
 * sponsor appears on more pages than a partner, and the strip shows whichever
 * page the clock is on.
 */
import { computed } from 'vue';
import type { ProgrammePayload, ProgrammeSponsor, SponsorStripZoneParams } from './programme-types';
import {
  scopedPerformances,
  selectPerformance,
  sponsorLogoUrl,
  sponsorPages,
  sponsorsForZone,
} from './programme';

const props = defineProps<{
  params: SponsorStripZoneParams;
  payload: ProgrammePayload;
  /** Injected so the visible page is deterministic and testable. */
  now: Date;
}>();

const performance = computed(() =>
  props.params.source === 'house'
    ? undefined
    : selectPerformance(scopedPerformances(props.payload, props.params), props.payload, props.now),
);

const pages = computed<ProgrammeSponsor[][]>(() =>
  sponsorPages(
    sponsorsForZone(props.params, props.payload, performance.value),
    props.params.maxVisible,
  ),
);

const visible = computed<ProgrammeSponsor[]>(() => {
  if (pages.value.length === 0) return [];
  const step = Math.floor(props.now.getTime() / (props.params.rotateSeconds * 1000));
  return pages.value[step % pages.value.length] ?? [];
});

function logo(sponsor: ProgrammeSponsor): string | undefined {
  return sponsorLogoUrl(sponsor, props.params.preferDarkLogo);
}
</script>

<template>
  <div
    v-if="visible.length > 0"
    class="lr42-sponsors"
    :class="`lr42-sponsors--${params.templateVariant}`"
  >
    <div v-for="sponsor in visible" :key="sponsor.id" class="lr42-sponsors__item">
      <img
        v-if="logo(sponsor)"
        :src="logo(sponsor)"
        :alt="sponsor.name"
        class="lr42-sponsors__logo"
      />
      <span v-else class="lr42-sponsors__name">{{ sponsor.name }}</span>
    </div>
  </div>
</template>

<style scoped>
.lr42-sponsors {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  gap: var(--lr42-gap, 2%);
  padding: var(--lr42-padding, 2%);
  background: var(--lr42-bg, transparent);
  color: var(--lr42-fg, #f6eedc);
  font-family: var(--lr42-font, Georgia, 'Times New Roman', serif);
  overflow: hidden;
}
.lr42-sponsors--stack {
  flex-direction: column;
}
.lr42-sponsors__item {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  display: grid;
  place-items: center;
  overflow: hidden;
}
.lr42-sponsors__logo {
  max-width: 100%;
  max-height: 100%;
  /* Contain, never cover: a cropped sponsor logo is a contractual problem. */
  object-fit: contain;
  display: block;
}
.lr42-sponsors__name {
  font-size: var(--lr42-body-size, clamp(11px, 1.4vw, 24px));
  color: var(--lr42-muted, rgba(246, 238, 220, 0.7));
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
